import React, { useEffect } from 'react'
import { Modal } from 'react-bootstrap'

interface AlertModalProps {
  show: boolean
  onHide: () => void
  icon: 'success' | 'error' | 'warning' | 'info' | 'message' | React.ReactNode
  title: string
  message: string
  buttonText?: string
  fullWidthButton?: boolean
  maxWidth?: string | number
  onConfirm?: () => void
}

export const AlertModal: React.FC<AlertModalProps> = ({
  show,
  onHide,
  icon,
  title,
  message,
  buttonText = 'Aceptar',
  fullWidthButton = false,
  maxWidth,
  onConfirm,
}) => {
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm()
    } else {
      onHide()
    }
  }

  // Aplicar estilos al modal-content cuando se muestra
  useEffect(() => {
    if (show) {
      const modalContent = document.querySelector('.modal-content') as HTMLElement
      if (modalContent) {
        modalContent.style.background = 'none'
        modalContent.style.boxShadow = 'none'
      }
    }
  }, [show])

  const renderIcon = () => {
    if (React.isValidElement(icon)) {
      return icon
    }

    switch (icon) {
      case 'success':
        return (
          <svg
            width='60'
            height='60'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            style={{ margin: '0 auto', display: 'block' }}
          >
            <path
              d='M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
              stroke='#00C051'
              strokeWidth='1'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        )
      case 'error':
        return (
          <svg
            width='60'
            height='60'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            style={{ margin: '0 auto', display: 'block' }}
          >
            <path
              d='M9 9L15 15M15 9L9 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
              stroke='#F4483F'
              strokeWidth='1'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        )
      case 'warning':
        return (
          <svg
            width='60'
            height='60'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            style={{ margin: '0 auto', display: 'block' }}
          >
            <path
              d='M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
              stroke='#F59E0B'
              strokeWidth='1'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        )
      case 'info':
        return (
          <svg
            width='60'
            height='60'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            style={{ margin: '0 auto', display: 'block' }}
          >
            <path
              d='M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
              stroke='#3B82F6'
              strokeWidth='1'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        )
      case 'message':
        return (
          <svg
            width='50'
            height='45'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            style={{ margin: '0 auto', display: 'block' }}
          >
            <path
              d='M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z'
              stroke='#007BC3'
              strokeWidth='1'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M22 6L12 13L2 6'
              stroke='#007BC3'
              strokeWidth='1'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        )
      default:
        return null
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered backdrop={true} keyboard={false}>
      <Modal.Dialog
        style={maxWidth ? { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth } : {}}
      >
        <Modal.Body className='p-5'>
          <div className='d-flex flex-column align-items-center'>
            <div className='mb-4'>{renderIcon()}</div>
            <h1
              className='text-center mb-3 fw-bold'
              style={{
                color: '#69778F',
                fontSize: '18px',
                fontFamily: 'sans-serif',
                lineHeight: '22px',
                letterSpacing: '2%',
              }}
            >
              {title}
            </h1>
            <p
              className='text-center mb-4'
              style={{
                color: '#69778F',
                fontWeight: 500,
                fontFamily: 'sans-serif',
                lineHeight: '22px',
                letterSpacing: '2%',
              }}
            >
              {message}
            </p>
            <button
              type='button'
              className={`btn btn-lg ${fullWidthButton ? 'w-100' : ''}`}
              style={{
                background: '#007BC3',
                color: 'white',
                maxWidth: fullWidthButton ? '100%' : '320px',
              }}
              onClick={handleConfirm}
            >
              {buttonText}
            </button>
          </div>
        </Modal.Body>
      </Modal.Dialog>
    </Modal>
  )
}
